import { forwardRef } from "react";
import type { ReactElement } from "react";
import type { Layout, Nummerierung } from "@kundeninfo/api-client";
import { Register, Reiter } from "@kundeninfo/ui/me-design";
import type { Entwurf, RegisterId } from "./layoutEntwurf.js";

export type { RegisterId } from "./layoutEntwurf.js";

/**
 * Die REGISTER der Gestaltung (Spec, Oberfläche, rechts): Marke, Seite, Sektionen,
 * Tabelle, Blöcke, Texte. Welche es gibt, sagt der Editor über `eintraege` — so wächst
 * die Liste mit K9/K10, ohne dass dieses Gerüst sich ändert.
 *
 * Seit dem Paket „Editor-Arbeitsdialog“ (Aufgabe 2) in ZWEI Teilen, die der Editor in der
 * Gestaltungsspalte unmittelbar übereinander setzt (Leiste, darunter das Panel in einer
 * Sperr-`fieldset`). Panel und Leiste kennen sich nur über `panelId`.
 */

export type PanelProps = {
  stand: Layout;
  entwurf: Entwurf;
  feldFehler: Record<string, string>;
  belegart: string;
  onEntwurf: (neu: Entwurf) => void;
  onFeldVerlassen: () => void;
  /** Überschriften-Nummerierung (Migration 048): die Schemata (null = lädt) und die Wahl im Entwurf
   *  (erfüllt sich nach Bestätigung und Schreiben — oder nach „Abbrechen"). */
  nummerierungen: readonly Nummerierung[] | null;
  onNummerierung: (id: string | null) => Promise<void>;
};

export type RegisterEintrag = {
  id: RegisterId; label: string; panel: (p: PanelProps) => ReactElement;
};

/** Das Element hinter `Register` — für `zumErstenFehler()` (Review-Fokus 3). */
export type ReiterleisteElement = HTMLElement & { zumErstenFehler(): string | null };

/** Id des Panels zu einem Register — zugleich die Kennung seines `me-reiter` (`fuer`):
 *  Das Register meldet sie in `me-reiter-gewaehlt` und erwartet sie in `aktiv`. */
export function panelId(id: RegisterId): string {
  return `lay-register-${id}`;
}

/** Der Reiter zu einer Kennung aus `me-reiter-gewaehlt`/`zumErstenFehler()`. */
export function registerAusKennung(
  eintraege: readonly RegisterEintrag[], kennung: string | null,
): RegisterId | null {
  return eintraege.find((e) => panelId(e.id) === kennung)?.id ?? null;
}

/**
 * Die Reiterleiste: `me-register` (klebend) mit einem `me-reiter` je Register (Hausregel
 * „Reiter nach unten offen" — die Mechanik bringt das Element mit). `role="tablist"`/
 * `role="tab"` stehen im Shadow DOM; `aria-label` am Host spiegelt me-register auf seine
 * tablist. `fuer` setzt dort `aria-controls`, das über die Schattengrenze nicht auf das
 * Panel zeigt — das Panel trägt seinen Namen deshalb selbst (`aria-label`).
 *
 * `fehler` je Reiter: die Zahl der Feldbefunde seines Panels (Zuordnung Feldpfad → Reiter
 * in layoutEntwurf.ts) — das Element zeigt sie in Fehlerfarbe, `zumErstenFehler()` springt
 * zum ersten Reiter mit Fehlern.
 */
export const LayoutReiterleiste = forwardRef<ReiterleisteElement, {
  eintraege: readonly RegisterEintrag[]; aktiv: RegisterId; onAktiv: (id: RegisterId) => void;
  fehler: Partial<Record<RegisterId, number>>;
}>(function LayoutReiterleiste(p, ref): ReactElement {
  const aktiv = p.eintraege.find((e) => e.id === p.aktiv) ?? p.eintraege[0];
  return (
    <Register ref={ref} className="lay__register-leiste" aria-label="Gestaltung" klebend
      {...(aktiv === undefined ? {} : { aktiv: panelId(aktiv.id) })}
      onReiterGewaehlt={(d) => {
        const gewaehlt = registerAusKennung(p.eintraege, d.id);
        if (gewaehlt !== null) p.onAktiv(gewaehlt);
      }}>
      {p.eintraege.map((e) => {
        const zahl = p.fehler[e.id] ?? 0;
        return (
          <Reiter key={e.id} fuer={panelId(e.id)} beschriftung={e.label}
            {...(zahl > 0 ? { fehler: zahl } : {})} />
        );
      })}
    </Register>
  );
});

/** Das Panel des aktiven Registers — im Körper des Arbeitsdialogs. */
export function LayoutRegisterPanel(p: PanelProps & {
  eintraege: readonly RegisterEintrag[]; aktiv: RegisterId;
}): ReactElement | null {
  const aktiv = p.eintraege.find((e) => e.id === p.aktiv) ?? p.eintraege[0];
  if (aktiv === undefined) return null;
  return (
    <div id={panelId(aktiv.id)} role="tabpanel" aria-label={aktiv.label}
      className="lay__registerinhalt">
      {aktiv.panel(p)}
    </div>
  );
}
